<template>
  <div class="cashier-page">
    <el-row :gutter="20" class="cashier-layout">
      <!-- 左侧：结账清单区 -->
      <el-col :xs="24" :sm="24" :md="8" :lg="8" class="left-pane">
        <div class="app-card-lg cashier-panel">
          <div class="panel-header">
            <h2 class="panel-title">
              结账清单（{{ totalQuantity }}件）
            </h2>
            <el-button class="btn-secondary" @click="clearCart" :disabled="!cartItems.length">
              清空购物车
            </el-button>
          </div>

          <div v-if="cartItems.length === 0" class="cart-empty">
            <i class="el-icon-shopping-cart-full cart-empty-icon"></i>
            <div class="cart-empty-text">请在右侧点击商品，加入结账清单</div>
          </div>

          <div v-else class="cart-list">
            <el-table
              :data="cartItems"
              class="app-table cashier-cart-table"
              :show-header="true"
              :border="false"
            >
              <el-table-column label="商品" min-width="45%">
                <template slot-scope="scope">
                  <div class="cart-item-name">
                    {{ scope.row.name }}
                  </div>
                </template>
              </el-table-column>

              <el-table-column label="数量" min-width="25%">
                <template slot-scope="scope">
                  <div class="qty-control">
                    <button class="qty-btn" @click="changeQty(scope.row, -1)">-</button>
                    <span class="qty-value">{{ scope.row.quantity }}</span>
                    <button class="qty-btn" @click="changeQty(scope.row, 1)">+</button>
                  </div>
                </template>
              </el-table-column>

              <el-table-column label="小计" min-width="30%">
                <template slot-scope="scope">
                  <div class="app-table-number">¥{{ (scope.row.price * scope.row.quantity).toFixed(2) }}</div>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <div class="cart-footer">
            <div class="cart-summary">
              <div>共 {{ totalQuantity }} 件商品</div>
              <div class="summary-line">
                <span class="summary-label">合计：</span>
                <span class="cart-total">¥{{ subtotalAmount.toFixed(2) }}</span>
              </div>
            </div>

            <div class="cart-actions">
              <el-button class="btn-secondary" @click="openCashDialog" :disabled="!canPayNow">
                现金收款
              </el-button>
              <el-button
                class="btn-primary"
                @click="openOnlineDialog"
                :disabled="!canPayNow"
              >
                收款 ¥{{ payableAmount.toFixed(2) }}
              </el-button>
            </div>
          </div>
        </div>
      </el-col>

      <!-- 中间：商品列表区 -->
      <el-col :xs="24" :sm="24" :md="10" :lg="10" class="middle-pane">
        <div class="app-card-lg cashier-panel">
          <div class="panel-header">
            <h2 class="panel-title">商品列表</h2>
            <el-input
              v-model="productKeyword"
              placeholder="搜索商品名称"
              clearable
              size="small"
              prefix-icon="el-icon-search"
              class="app-input product-search"
            />
          </div>

          <div class="product-list">
            <div
              v-for="p in filteredProducts"
              :key="p.id"
              class="product-card"
              :class="{ disabled: p.stock <= 0 }"
              @click="addToCart(p)"
            >
              <div class="product-info">
                <div class="product-name">{{ p.name }}</div>
                <div class="product-meta">
                  <span class="product-price">¥{{ Number(p.price).toFixed(2) }}</span>
                  <span class="product-stock">库存 {{ Math.max(0, p.stock) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </el-col>

      <!-- 右侧：会员登录 + 商品分类 + 快捷操作 -->
      <el-col :xs="24" :sm="24" :md="6" :lg="6" class="right-pane">
        <div class="app-card-lg cashier-panel">
          <div class="member-login-top">
            <div class="member-login-title">会员登录</div>

            <el-button class="btn-primary scan-member-btn" size="small" @click="openMemberLogin">
              扫会员码登录
            </el-button>

            <el-autocomplete
              v-model="memberKeyword"
              class="member-search"
              :fetch-suggestions="queryMembers"
              :debounce="300"
              placeholder="请输入会员码/手机号/姓名"
              clearable
              value-key="displayLabel"
              :popper-append-to-body="false"
              @select="handleMemberSelect"
            />

            <div v-if="memberKeyword && memberNoMatch" class="member-no-match-hint">
              暂无匹配会员，可前往小程序注册会员
            </div>

            <div v-if="member" class="member-info">
              <div class="member-info-row">
                姓名：<strong>{{ member.name }}</strong>
              </div>
              <div class="member-info-row">
                会员码：<strong>{{ shortMemberCode(member.member_code) }}</strong>
              </div>
              <div class="member-info-row">
                会员等级：<strong>{{ member.level_name }}</strong>
              </div>
              <div class="member-info-row">
                可用券：<strong>{{ member.voucher_count }}</strong> 张
              </div>
            </div>
          </div>

          <div class="panel-header panel-header-small">
            <h2 class="panel-title">商品分类</h2>
          </div>

          <div class="category-list">
            <button
              v-for="tag in categoryTags"
              :key="tag"
              class="category-tag"
              :class="{ active: activeCategoryTag === tag }"
              @click="activeCategoryTag = tag"
            >
              {{ tag }}
            </button>
          </div>

          <div class="side-section">
            <div class="side-title">快捷操作</div>
            <el-button
              class="btn-secondary"
              size="small"
              @click="clearCart"
              :disabled="!cartItems.length"
            >
              清空购物车
            </el-button>
          </div>
        </div>
      </el-col>
    </el-row>

    <!-- 会员登录：扫码弹窗 -->
    <el-dialog
      title="会员登录（扫会员码）"
      :visible.sync="memberLoginVisible"
      width="420px"
      :before-close="handleMemberDialogClose"
      center
    >
      <div class="member-login-dialog">
        <el-input
          v-model="memberToken"
          placeholder="可手动输入会员码 token（扫码优先）"
          clearable
        />

        <div class="member-login-actions">
          <el-button class="btn-secondary" size="small" @click="startMemberScan">
            开始扫码
          </el-button>
          <el-button class="btn-primary" size="small" @click="verifyMemberToken">
            验证并登录
          </el-button>
        </div>

        <div class="scan-area" v-show="memberScanning">
          <video ref="scanVideo" class="scan-video" playsinline></video>
          <div class="scan-hint">请对准二维码，识别后将自动校验</div>
        </div>

        <div v-if="scanError" class="scan-error">{{ scanError }}</div>
      </div>
    </el-dialog>

    <!-- 现金收款弹窗 -->
    <el-dialog
      title="现金收款"
      :visible.sync="cashDialogVisible"
      width="460px"
      :close-on-click-modal="false"
      center
    >
      <div class="pay-dialog">
        <div class="pay-row">
          <span class="pay-label">应付：</span>
          <span class="pay-value">¥{{ payableAmount.toFixed(2) }}</span>
        </div>

        <div class="pay-row">
          <span class="pay-label">实收金额：</span>
          <el-input-number
            v-model="cashReceived"
            :min="0"
            :step="1"
            :precision="2"
            class="pay-input"
          />
        </div>

        <div class="pay-row pay-change">
          <span class="pay-label">找零：</span>
          <span class="pay-change-value">
            ¥{{ (cashReceived - payableAmount).toFixed(2) }}
          </span>
        </div>

        <div class="pay-actions">
          <el-button class="btn-secondary" @click="cashDialogVisible = false">取消</el-button>
          <el-button class="btn-primary" :loading="paySubmitting" @click="confirmCashPay" :disabled="!canCashPay">
            确认收款
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 在线收款弹窗 -->
    <el-dialog
      title="选择支付方式"
      :visible.sync="onlineDialogVisible"
      width="520px"
      :close-on-click-modal="false"
      center
    >
      <div class="pay-dialog">
        <div class="pay-row">
          <span class="pay-label">应付：</span>
          <span class="pay-value">¥{{ payableAmount.toFixed(2) }}</span>
        </div>

        <div class="pay-method-block">
          <div class="pay-label">支付方式：</div>
          <el-radio-group v-model="onlinePaymentMethod" class="pay-method-group">
            <el-radio-button label="wechat">微信</el-radio-button>
            <el-radio-button label="alipay">支付宝</el-radio-button>
            <el-radio-button label="bank_card">银行卡</el-radio-button>
          </el-radio-group>
        </div>

        <div class="pay-actions">
          <el-button class="btn-secondary" @click="onlineDialogVisible = false">取消</el-button>
          <el-button class="btn-primary" :loading="paySubmitting" @click="confirmOnlinePay" :disabled="!canPayNow">
            确认收款
          </el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { formatApiError } from '../utils/apiError'

const MEMBER_CACHE_KEY = 'eating_cashier_member_cache_v1'

export default {
  name: 'CashierSystem',
  data() {
    return {
      productKeyword: '',
      activeCategoryTag: '全部',
      products: [],

      cartItems: [],

      // 会员输入/选择
      memberKeyword: '',
      memberNoMatch: false,
      member: null,

      // 扫码弹窗
      memberLoginVisible: false,
      memberToken: '',
      memberScanning: false,
      scanError: '',
      scanStream: null,

      // 现金支付
      cashDialogVisible: false,
      cashReceived: 0,

      // 在线支付
      onlineDialogVisible: false,
      onlinePaymentMethod: 'wechat',

      paySubmitting: false
    }
  },
  computed: {
    categoryTags() {
      return ['全部', '随心订', '饮品']
    },
    subtotalAmount() {
      return this.cartItems.reduce((sum, it) => sum + (it.price * it.quantity), 0)
    },
    totalQuantity() {
      return this.cartItems.reduce((sum, it) => sum + it.quantity, 0)
    },
    payableAmount() {
      return Math.max(0, this.subtotalAmount)
    },
    canPayNow() {
      return !!this.member && this.cartItems.length > 0
    },
    canCashPay() {
      return this.canPayNow && this.cashReceived >= this.payableAmount
    },
    filteredProducts() {
      const kw = (this.productKeyword || '').trim().toLowerCase()
      return this.products.filter((p) => {
        const tag = this.mapProductTag(p)
        const matchTag = this.activeCategoryTag === '全部' || tag === this.activeCategoryTag
        const matchKw = !kw || String(p.name || '').toLowerCase().includes(kw)
        return matchTag && matchKw
      })
    }
  },
  mounted() {
    this.loadProducts()
    this.restoreMemberFromCache()
  },
  methods: {
    safeNumber(v, fallback = 0) {
      const n = Number(v)
      return Number.isFinite(n) ? n : fallback
    },
    shortMemberCode(code) {
      const s = String(code || '')
      if (!s) return '—'
      if (s.length <= 8) return s
      return `${s.slice(0, 2)}…${s.slice(-4)}`
    },
    mapProductTag(p) {
      const c = String(p.category || '')
      // DB 常见值可能是「饮料/饮品/套餐/轻食」，统一映射为「饮品/随心订」
      if (c.includes('饮') || c.includes('drink') || c.includes('Drink')) return '饮品'
      return '随心订'
    },
    async loadProducts() {
      try {
        const resp = await this.$api.get('/cashier/products', { params: { category: '全部' } })
        if (resp && resp.code === 200 && Array.isArray(resp.data)) {
          this.products = resp.data
          return
        }
        this.$message.warning('商品接口返回异常，将展示空列表')
      } catch (e) {
        console.error('[cashier][products] error:', e)
        this.$message.error(`${formatApiError(e, '加载商品失败')}`)
      }
    },

    getCartProductStock(productId) {
      const p = this.products.find((x) => String(x.id) === String(productId))
      return p ? Math.max(0, Math.floor(Number(p.stock || 0))) : 0
    },
    addToCart(product) {
      const stock = Math.max(0, Math.floor(Number(product.stock || 0)))
      if (stock <= 0) {
        this.$message.warning('该商品库存不足，无法加入结账清单')
        return
      }

      const idx = this.cartItems.findIndex((it) => String(it.id) === String(product.id))
      if (idx >= 0) {
        const nextQty = this.cartItems[idx].quantity + 1
        if (nextQty > stock) {
          this.$message.warning(`库存不足：${product.name} 剩余 ${stock}`)
          return
        }
        this.cartItems[idx].quantity = nextQty
      } else {
        this.cartItems.push({
          id: product.id,
          name: product.name,
          price: Number(product.price || 0),
          quantity: 1
        })
      }
    },
    changeQty(row, delta) {
      const idx = this.cartItems.findIndex((it) => String(it.id) === String(row.id))
      if (idx < 0) return

      const stock = this.getCartProductStock(this.cartItems[idx].id)
      let next = this.cartItems[idx].quantity + delta
      if (!Number.isFinite(next)) next = 1
      if (next <= 0) {
        this.cartItems.splice(idx, 1)
        return
      }
      if (stock > 0) next = Math.min(next, stock)
      this.cartItems[idx].quantity = next
    },
    clearCart() {
      this.cartItems = []
    },

    openMemberLogin() {
      this.memberLoginVisible = true
      this.memberToken = this.memberToken || ''
      this.scanError = ''
    },
    async verifyMemberToken() {
      const token = String(this.memberToken || '').trim()
      if (!token) {
        this.$message.warning('请先输入会员码 token（或点击开始扫码）')
        return
      }
      await this.loginByMemberCode(token)
    },

    async queryMembers(queryString, cb) {
      const kw = String(queryString || '').trim()
      if (!kw) {
        this.memberNoMatch = false
        cb([])
        return
      }

      try {
        const resp = await this.$api.get('/cashier/members', { params: { keyword: kw } })
        const list = resp && resp.code === 200 && Array.isArray(resp.data) ? resp.data : []
        if (!list.length) {
          this.memberNoMatch = true
          cb([{ displayLabel: '暂无匹配会员', user_id: '', isEmpty: true }])
          return
        }

        this.memberNoMatch = false
        cb(list)
      } catch (e) {
        console.error('[cashier][members] error:', e)
        this.memberNoMatch = false
        cb([])
      }
    },

    async handleMemberSelect(item) {
      if (!item || item.isEmpty) return
      if (!item.user_id) return

      const nextMember = {
        user_id: item.user_id,
        member_code: item.member_code || '',
        name: item.name || item.username || '会员',
        phone: item.phone || '',
        level_code: item.level_code || 'S0',
        level_name: item.level_name || item.level_code || 'S0',
        voucher_count: Number(item.voucher_count || 0)
      }

      this.member = nextMember
      this.memberKeyword = item.displayLabel
      this.persistMemberToCache(nextMember)
    },

    persistMemberToCache(member) {
      try {
        localStorage.setItem(MEMBER_CACHE_KEY, JSON.stringify(member || {}))
      } catch (e) {
        // 忽略本地存储失败
      }
    },
    async restoreMemberFromCache() {
      try {
        const raw = localStorage.getItem(MEMBER_CACHE_KEY)
        if (!raw) return
        const cached = JSON.parse(raw)
        if (!cached || !cached.user_id) return
        this.member = cached
        this.memberKeyword = cached.name ? cached.name : ''
        // 刷新可用券数量，避免用旧数据
        if (cached.member_code) await this.loginByMemberCode(cached.member_code)
      } catch (e) {
        // ignore
      }
    },

    async loginByMemberCode(code) {
      try {
        const resp = await this.$api.get('/cashier/members', { params: { keyword: code } })
        const list = resp && resp.code === 200 && Array.isArray(resp.data) ? resp.data : []
        if (!list.length) {
          this.$message.error('未找到该会员信息，请检查会员码/手机号/姓名')
          return
        }
        const exact = list.find((x) => String(x.member_code) === String(code))
        const picked = exact || list[0]
        await this.handleMemberSelect(picked)
        this.memberLoginVisible = false
        this.stopMemberScan()
        this.$message.success('会员登录成功')
      } catch (e) {
        console.error('[cashier][login] error:', e)
        this.$message.error('会员登录失败，请重试')
      }
    },

    async startMemberScan() {
      if (this.memberScanning) return
      this.scanError = ''

      if (!navigator.mediaDevices || !window.BarcodeDetector) {
        this.$message.warning('当前浏览器不支持扫码，请手动输入会员码 token')
        return
      }

      const videoEl = this.$refs.scanVideo
      if (!videoEl) return

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
        this.scanStream = stream
        videoEl.srcObject = stream
        await videoEl.play()

        this.memberScanning = true

        const detector = new window.BarcodeDetector({ formats: ['qr_code'] })
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d', { willReadFrequently: true })

        const tick = async () => {
          if (!this.memberScanning) return
          if (!videoEl.videoWidth || !videoEl.videoHeight) {
            requestAnimationFrame(tick)
            return
          }
          canvas.width = videoEl.videoWidth
          canvas.height = videoEl.videoHeight
          ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height)

          try {
            const barcodes = await detector.detect(canvas)
            if (barcodes && barcodes.length) {
              const raw = barcodes[0].rawValue || ''
              if (raw) {
                this.memberToken = raw
                this.stopMemberScan()
                await this.loginByMemberCode(raw)
                return
              }
            }
          } catch (e) {
            // ignore detect error
          }

          setTimeout(tick, 300)
        }

        tick()
      } catch (e) {
        this.scanError = '无法打开摄像头，请检查权限'
        this.stopMemberScan()
      }
    },
    stopMemberScan() {
      this.memberScanning = false
      if (this.scanStream) {
        this.scanStream.getTracks().forEach((t) => t.stop())
        this.scanStream = null
      }
      const videoEl = this.$refs.scanVideo
      if (videoEl) videoEl.srcObject = null
    },
    handleMemberDialogClose(done) {
      this.stopMemberScan()
      this.scanError = ''
      if (done) done()
    },

    openCashDialog() {
      if (!this.canPayNow) {
        this.$message.warning('请先完成会员登录')
        return
      }
      if (!this.cartItems.length) return
      this.cashReceived = Math.max(0, Number(this.payableAmount.toFixed(2)))
      this.cashDialogVisible = true
    },
    openOnlineDialog() {
      if (!this.canPayNow) {
        this.$message.warning('请先完成会员登录')
        return
      }
      if (!this.cartItems.length) return
      this.onlinePaymentMethod = 'wechat'
      this.onlineDialogVisible = true
    },

    async confirmCashPay() {
      await this.createOrder('cash')
    },
    async confirmOnlinePay() {
      const m = this.onlinePaymentMethod
      await this.createOrder(m)
    },

    async createOrder(payment_method) {
      if (!this.canPayNow) {
        this.$message.warning('请先完成会员登录')
        return
      }
      if (!this.cartItems.length) return

      this.paySubmitting = true
      try {
        const payload = {
          member_user_id: this.member.user_id,
          payment_method,
          paid_amount: this.payableAmount,
          cash_received: payment_method === 'cash' ? this.cashReceived : undefined,
          items: this.cartItems.map((it) => ({
            product_id: it.id,
            quantity: it.quantity
          }))
        }

        const resp = await this.$api.post('/cashier/orders', payload)
        if (resp && (resp.code === 201 || resp.code === 200)) {
          this.$message.success('收款成功')
          this.clearCart()
          this.cashDialogVisible = false
          this.onlineDialogVisible = false
          await this.loadProducts()
          if (this.member && this.member.member_code) {
            await this.loginByMemberCode(this.member.member_code)
          }
          return
        }

        this.$message.error((resp && resp.message) || '收款失败')
      } catch (e) {
        console.error('[cashier][order] error:', e)
        this.$message.error(formatApiError(e, '收款失败，请重试'))
      } finally {
        this.paySubmitting = false
      }
    }
  }
}

function safeNumber(v, fallback = 0) {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}
</script>

<style scoped>
.cashier-page {
  padding: 24px;
  background: var(--page-bg);
}

.cashier-layout {
  min-height: calc(100vh - 120px);
}

.left-pane,
.middle-pane,
.right-pane {
  height: 100%;
}

.cashier-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-soft);
  border: 1px solid var(--border-color);
  padding: 24px;
  box-sizing: border-box;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.panel-header-small {
  margin-top: 6px;
}

.panel-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-strong);
}

.panel-title strong {
  color: var(--primary-color);
}

.cart-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  text-align: center;
}

.cart-empty-icon {
  font-size: 40px;
  margin-bottom: 10px;
  color: var(--primary-color);
}

.cart-empty-text {
  font-size: 12px;
  line-height: 1.5;
  max-width: 180px;
}

.cart-list {
  flex: 1;
  overflow: hidden;
}

.cashier-cart-table {
  margin-top: 4px;
}

.cart-footer {
  margin-top: 12px;
}

.cart-summary {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
  font-size: 14px;
  color: var(--text-body);
}

.summary-line {
  display: flex;
  justify-content: space-between;
}

.summary-label {
  color: #334155;
}

.cart-total {
  font-size: 18px;
  font-weight: 700;
  color: var(--primary-color);
}

.discount-line .discount-value {
  color: #a855f7;
  font-weight: 700;
}

.payable-line .payable-value {
  color: #165dff;
  font-weight: 800;
}

.cart-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.product-search {
  width: 220px;
}

.product-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  grid-gap: 16px;
  margin-top: 12px;
}

.product-card {
  display: flex;
  padding: 10px 12px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid var(--border-color);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease,
    border-color 0.2s ease, opacity 0.2s ease;
  min-height: 72px;
}

.product-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-soft);
  background: #f5f3ff;
  border-color: var(--primary-color);
}

.product-card.disabled {
  opacity: 0.45;
  cursor: not-allowed;
  background: #f3f4f6;
}

.product-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.product-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-strong);
  margin-bottom: 4px;
}

.product-meta {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
}

.product-price {
  color: var(--brand-color);
  font-weight: 600;
}

.product-stock {
  color: var(--text-subtle);
}

.member-login-top {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.member-login-title {
  font-weight: 800;
  font-size: 14px;
  color: #1d2129;
}

.scan-member-btn {
  width: 100%;
  justify-content: center;
}

.member-search {
  width: 100%;
}

.member-no-match-hint {
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.4;
  margin-top: -4px;
}

.member-info {
  border-top: 1px dashed rgba(99, 102, 241, 0.25);
  padding-top: 10px;
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #475569;
}

.member-info-row strong {
  color: #111827;
  font-weight: 700;
}

.category-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.category-tag {
  border-radius: 8px;
  padding: 6px 12px;
  background: #ffffff;
  border: 1px solid var(--border-color);
  color: #374151;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
}

.category-tag:hover {
  background: #eef2ff;
}

.category-tag.active {
  background: #165dff;
  color: #ffffff;
  border-color: #165dff;
}

.side-section {
  margin-top: auto;
  padding-top: 14px;
}

.side-title {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 10px;
}

/* 数量控制 */
.qty-control {
  display: inline-flex;
  align-items: center;
  background: #f1f5f9;
  border-radius: 6px;
  padding: 2px 4px;
  border: 1px solid var(--border-color);
}

.qty-btn {
  border: none;
  background: transparent;
  width: 22px;
  height: 22px;
  font-size: 14px;
  cursor: pointer;
  color: #475569;
}

.qty-btn:active {
  transform: scale(0.95);
}

.qty-value {
  min-width: 26px;
  text-align: center;
  font-size: 14px;
  color: var(--text-body);
}

.cart-item-name {
  color: var(--text-body);
  word-break: break-all;
  white-space: normal;
}

/* 扫码弹窗 */
.member-login-dialog {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.member-login-actions {
  display: flex;
  gap: 10px;
}

.scan-area {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.scan-video {
  width: 100%;
  max-height: 240px;
  background: #0b1220;
  border-radius: 8px;
}

.scan-hint {
  font-size: 12px;
  color: #64748b;
}

.scan-error {
  font-size: 12px;
  color: #ef4444;
}

/* 支付弹窗 */
.pay-dialog {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.pay-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
}

.pay-label {
  color: #334155;
}

.pay-value {
  font-weight: 800;
  color: #165dff;
}

.pay-change-value {
  font-weight: 800;
  color: #16a34a;
}

.pay-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.pay-method-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pay-method-group {
  display: flex;
  gap: 12px;
}
</style>

